import { useState, type ReactNode } from "react";
import {
  CLONABLE_TIERS,
  HIERARCHY_TIERS,
  MAX_TIER_LEVELS,
  ROLE_LABELS,
  defaultRoleName,
  isStaffNode,
  nodeOf,
  reportsTo,
  roleKey,
  type ClonableTier,
  type HierarchyTier,
} from "@gatiq/shared";
import {
  ArrowDown,
  ArrowUp,
  Briefcase,
  Building2,
  Check,
  Copy,
  Factory,
  GripVertical,
  Hammer,
  Pencil,
  Plus,
  ShoppingBag,
  Store,
  Truck,
  X,
} from "lucide-react";
import { useLanguage } from "@/i18n/language";

export interface TierMeta {
  tier: HierarchyTier;
  label: string;
  hindiLabel: string;
  desc: string;
  icon: typeof Building2;
}

export const TIER_DEFINITIONS: Record<HierarchyTier, TierMeta> = {
  depot: {
    tier: "depot",
    label: "Regional depots",
    hindiLabel: "क्षेत्रीय डिपो / गोदाम",
    desc: "State-level warehouses holding bulk stock.",
    icon: Building2,
  },
  sales_rep: {
    tier: "sales_rep",
    label: "Sales reps",
    hindiLabel: "सेल्स ऑफिसर / ASO",
    desc: "Area sales team managing territories and distributors.",
    icon: Briefcase,
  },
  distributor: {
    tier: "distributor",
    label: "Distributors",
    hindiLabel: "डिस्ट्रिब्यूटर / मुख्य वितरक",
    desc: "City stockists supplying dealers and retail counters.",
    icon: Truck,
  },
  sub_distributor: {
    tier: "sub_distributor",
    label: "Sub-distributors",
    hindiLabel: "सब-डिस्ट्रिब्यूटर / उप-वितरक",
    desc: "Local wholesalers between distributors and counters.",
    icon: Store,
  },
  counter: {
    tier: "counter",
    label: "Retail counters",
    hindiLabel: "दुकानदार / रिटेल डीलर्स",
    desc: "Shops selling to carpenters; they sign karigars up.",
    icon: ShoppingBag,
  },
  karigar: {
    tier: "karigar",
    label: "Karigars",
    hindiLabel: "कारीगर / बढ़ई / कांट्रैक्टर",
    desc: "Carpenters who scan product QR codes for points.",
    icon: Hammer,
  },
};

export interface PresetTemplate {
  name: string;
  hindiName: string;
  desc: string;
  tiers: HierarchyTier[];
}

export const HIERARCHY_PRESETS: PresetTemplate[] = [
  {
    name: "Enterprise (6-tier)",
    hindiName: "सम्पूर्ण (6-स्तर)",
    desc: "Depot → Sales rep → Distributor → Sub-distributor → Counter → Karigar",
    tiers: ["depot", "sales_rep", "distributor", "sub_distributor", "counter", "karigar"],
  },
  {
    name: "Regional (5-tier)",
    hindiName: "क्षेत्रीय (5-स्तर)",
    desc: "Depot → Sales rep → Distributor → Counter → Karigar",
    tiers: ["depot", "sales_rep", "distributor", "counter", "karigar"],
  },
  {
    name: "Classic (4-tier)",
    hindiName: "क्लासिक (4-स्तर)",
    desc: "Sales rep → Distributor → Counter → Karigar",
    tiers: ["sales_rep", "distributor", "counter", "karigar"],
  },
  {
    name: "Depot direct",
    hindiName: "डिपो से सीधे",
    desc: "Depot → Counter → Karigar",
    tiers: ["depot", "counter", "karigar"],
  },
  {
    name: "Factory direct",
    hindiName: "फैक्ट्री से सीधे",
    desc: "Counter → Karigar, supplied by the head office",
    tiers: ["counter", "karigar"],
  },
];

export interface HierarchyDragDropBuilderProps {
  /**
   * The chain top to bottom as steps: `depot`, `sales_rep:1`, `distributor:1`,
   * `sales_rep:2`, `counter`… A copied level is a step of its own, so it can
   * sit anywhere below a sales role — Sales Rep 2 under a Distributor (D-16).
   */
  value: string[];
  onChange: (chain: string[]) => void;
  orgName?: string;
  showPresets?: boolean;
  /** Show Copy on sales roles. */
  copies?: boolean;
  /** What the org calls each sales role, keyed by `roleKey` ("sales_rep:2" → "Field Officer"). */
  names?: Record<string, string>;
  /** Omit to show the names read-only. */
  onNamesChange?: (names: Record<string, string>) => void;
}

/** The names worth saving: trimmed, with blanks dropped so those levels keep their default. */
export function chosenRoleNames(names: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(names)
      .map(([key, name]) => [key, name.trim()] as const)
      .filter(([, name]) => name.length > 0),
  );
}

/** A step for a tier added fresh: level 1 of a sales role, or the tier itself. */
function stepFor(tier: HierarchyTier): string {
  return (CLONABLE_TIERS as readonly string[]).includes(tier) ? roleKey(tier, 1) : tier;
}

/* Reads as the row's title until hovered or focused, then as the field it is. */
const NAME_INPUT =
  "-ml-1.5 h-7 w-full max-w-72 min-w-0 truncate rounded-md border border-transparent bg-transparent px-1.5 " +
  "text-sm font-medium text-ink placeholder:text-ink transition-[border-color,background-color,box-shadow] " +
  "hover:border-rule focus:border-brand focus:bg-raised focus:shadow-[0_0_0_3px_var(--gq-color-1-ring)] " +
  "focus:outline-none focus:placeholder:text-muted/70";

/**
 * Builds an organization's distribution chain.
 *
 * One list, read top to bottom: the head office, then each step in the order
 * stock flows. Copying a sales role adds another level of it as a step of its
 * own, directly below; move it anywhere and it reports to the nearest sales
 * role above it. A tier is either in the chain (numbered, with its controls)
 * or not (a quiet "add" chip underneath).
 *
 * Fully controlled by `value`, so a parent resetting or loading the chain is
 * always reflected.
 */
export function HierarchyDragDropBuilder({
  value,
  onChange,
  orgName = "Organization",
  showPresets = true,
  copies = true,
  names = {},
  onNamesChange,
}: HierarchyDragDropBuilderProps) {
  const { t } = useLanguage();
  const chain = value.filter((node) => HIERARCHY_TIERS.includes(nodeOf(node).tier));
  const inUse = new Set(chain.map((node) => nodeOf(node).tier));
  const unused = HIERARCHY_TIERS.filter((tier) => !inUse.has(tier));
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const label = (tier: HierarchyTier) => t(TIER_DEFINITIONS[tier].hindiLabel, TIER_DEFINITIONS[tier].label);
  const levelsOfTier = (tier: string) => chain.filter((node) => nodeOf(node).tier === tier).length;
  const nameOf = (node: string): string => {
    const { tier, level } = nodeOf(node);
    return names[node]?.trim() || defaultRoleName(tier as ClonableTier, level, levelsOfTier(tier));
  };

  function dropNames(keys: string[]): void {
    if (!onNamesChange || !keys.some((k) => k in names)) return;
    const rest = { ...names };
    for (const k of keys) delete rest[k];
    onNamesChange(rest);
  }

  function move(from: number, to: number): void {
    if (to < 0 || to >= chain.length || from === to) return;
    const next = [...chain];
    const [moved] = next.splice(from, 1);
    if (moved) next.splice(to, 0, moved);
    onChange(next);
  }

  /** Level 1 takes its copies with it; a copy comes off only from the top level down, so numbers never skip. */
  function remove(node: string): void {
    const { tier, level } = nodeOf(node);
    const gone = isStaffNode(node) && level === 1 ? chain.filter((n) => nodeOf(n).tier === tier) : [node];
    onChange(chain.filter((n) => !gone.includes(n)));
    dropNames(gone);
  }

  /** The copy lands directly below the step it was made from. */
  function copy(node: string): void {
    const { tier } = nodeOf(node);
    const n = levelsOfTier(tier);
    if (n >= MAX_TIER_LEVELS) return;
    const next = [...chain];
    next.splice(chain.indexOf(node) + 1, 0, roleKey(tier, n + 1));
    onChange(next);
  }

  /** A returning tier goes where it naturally sits, not at the bottom. */
  function add(tier: HierarchyTier): void {
    const rank = HIERARCHY_TIERS.indexOf(tier);
    const at = chain.findIndex((node) => HIERARCHY_TIERS.indexOf(nodeOf(node).tier) > rank);
    const next = [...chain];
    next.splice(at === -1 ? next.length : at, 0, stepFor(tier));
    onChange(next);
  }

  const activePreset = HIERARCHY_PRESETS.find(
    (p) => p.tiers.length === chain.length && p.tiers.every((x, i) => chain[i] === stepFor(x)),
  );

  return (
      <div className="rounded-lg border-token border-rule bg-raised shadow-card">
        <div className="flex items-center justify-between gap-3 border-b border-rule/70 px-4 py-3">
          <p className="font-title text-sm font-semibold text-ink">{t("वितरण श्रृंखला", "Distribution chain")}</p>
          <span className="shrink-0 rounded-full bg-ink/[0.05] px-2.5 py-1 text-xs font-medium text-muted tabular-nums">
            {chain.length} {t("भूमिकाएँ", chain.length === 1 ? "role" : "roles")}
          </span>
        </div>

      {showPresets ? (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-rule/70 px-4 py-2.5">
          <span className="mr-1 text-xs text-muted">{t("टेम्पलेट:", "Template")}</span>
            {HIERARCHY_PRESETS.map((preset) => {
              const active = preset === activePreset;
              return (
                <button
                  aria-pressed={active}
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    active
                      ? "bg-brand-light text-brand-ink ring-1 ring-brand-border ring-inset"
                      : "bg-ink/[0.04] text-ink hover:bg-ink/[0.08]"
                  }`}
                  key={preset.name}
                  onClick={() => {
                    onChange(preset.tiers.map(stepFor));
                    dropNames(Object.keys(names));
                  }}
                  title={preset.desc}
                  type="button"
                >
                  {active ? <Check aria-hidden="true" className="size-3.5" /> : null}
                  {t(preset.hindiName, preset.name)}
                </button>
              );
            })}
        </div>
      ) : null}

        <ol className="relative px-4 py-3">
          {/* The spine: one rule behind the step markers joins the chain. */}
          <span aria-hidden="true" className="absolute top-7 bottom-7 left-[2.375rem] w-px bg-rule" />

          <li className="relative flex items-center gap-3 py-1.5">
            <span className="w-5 shrink-0" />
            <span className="relative grid size-8 shrink-0 place-items-center rounded-md bg-brand text-on-brand shadow-[inset_0_1px_0_rgb(255_255_255/0.18)]">
              <Factory aria-hidden="true" className="size-4" />
            </span>
            <p className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">{orgName}</p>
          </li>

          {chain.map((node, index) => {
            const { tier, level } = nodeOf(node);
            const meta = TIER_DEFINITIONS[tier];
            const Icon = meta.icon;
            const staff = isStaffNode(node);
            const n = levelsOfTier(tier);
            const manager = staff && level > 1 ? reportsTo(chain, node) : null;
            const orphan = staff && level > 1 && !manager;
            // Copies come off from the highest level down.
            const removable = !staff || level === 1 || level === n;
            const title = staff ? nameOf(node) : label(tier);
            const fallback = staff ? defaultRoleName(tier as ClonableTier, level, n) : "";
            const renamed = staff && Boolean(names[node]?.trim());
            return (
              <li
                className={`relative flex items-center gap-3 rounded-md py-1.5 transition-colors ${
                  dragOver === index && dragFrom !== index ? "bg-brand-light/70" : ""
                } ${dragFrom === index ? "opacity-50" : ""}`}
                key={node}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(index);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragFrom !== null) move(dragFrom, index);
                  setDragFrom(null);
                  setDragOver(null);
                }}
              >
                {/* Only the handle drags, so the name field stays typeable. */}
                <span
                  aria-hidden="true"
                  className="grid w-5 shrink-0 cursor-grab place-items-center text-muted/60 active:cursor-grabbing"
                  draggable
                  onDragEnd={() => {
                    setDragFrom(null);
                    setDragOver(null);
                  }}
                  onDragStart={(e) => {
                    setDragFrom(index);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                >
                  <GripVertical className="size-4" />
                </span>
                <span
                  className={`relative grid size-8 shrink-0 place-items-center rounded-md ring-1 ${
                    staff && level > 1 ? "bg-brand-light text-brand-ink ring-brand-border" : "bg-raised text-ink ring-rule"
                  }`}
                >
                  <Icon aria-hidden="true" className="size-4" />
                  <span className="absolute -top-1.5 -right-1.5 grid size-4 place-items-center rounded-full bg-brand font-title text-[9px] font-bold text-on-brand">
                    {index + 1}
                  </span>
                </span>

                <div className="min-w-0 flex-1">
                  {staff && onNamesChange ? (
                    <label className="group/name flex items-center gap-1">
                      <input
                        aria-label={t("भूमिका का नाम", `Name for ${fallback}`)}
                        className={NAME_INPUT}
                        maxLength={120}
                        onChange={(e) => onNamesChange({ ...names, [node]: e.target.value })}
                        placeholder={fallback}
                        value={names[node] ?? ""}
                      />
                      <Pencil
                        aria-hidden="true"
                        className="size-3 shrink-0 text-muted opacity-0 transition-opacity group-hover/name:opacity-100"
                      />
                    </label>
                  ) : (
                    <p className="truncate text-sm font-medium text-ink">{title}</p>
                  )}
                  {orphan ? (
                    <p className="truncate text-xs text-danger">
                      {t("इसके ऊपर कोई सेल्स भूमिका होनी चाहिए", "Needs a sales role above it")}
                    </p>
                  ) : manager ? (
                    <p className="truncate text-xs text-muted">
                      {t(`${nameOf(manager)} को रिपोर्ट करता है`, `Reports to ${nameOf(manager)}`)}
                    </p>
                  ) : renamed ? (
                    <p className="truncate text-xs text-muted">{ROLE_LABELS[tier as ClonableTier]}</p>
                  ) : null}
                </div>

                {staff && copies ? (
                  <button
                    aria-label={
                      n >= MAX_TIER_LEVELS
                        ? t("अधिकतम 3 स्तर", `Up to ${MAX_TIER_LEVELS} levels`)
                        : t("भूमिका कॉपी करें", `Copy ${title} — adds ${defaultRoleName(tier as ClonableTier, n + 1, n + 1)} below`)
                    }
                    className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-ink ring-1 ring-rule transition-colors ring-inset hover:bg-brand-light hover:text-brand-ink hover:ring-brand-border disabled:pointer-events-none disabled:opacity-40"
                    disabled={n >= MAX_TIER_LEVELS}
                    onClick={() => copy(node)}
                    title={n >= MAX_TIER_LEVELS ? t("अधिकतम 3 स्तर", `Up to ${MAX_TIER_LEVELS} levels`) : undefined}
                    type="button"
                  >
                    <Copy aria-hidden="true" className="size-3.5" />
                    {t("कॉपी", "Copy")}
                  </button>
                ) : null}

                <div className="flex shrink-0 items-center">
                  <IconButton disabled={index === 0} label={t("ऊपर ले जाएँ", `Move ${title} up`)} onClick={() => move(index, index - 1)}>
                    <ArrowUp className="size-3.5" />
                  </IconButton>
                  <IconButton
                    disabled={index === chain.length - 1}
                    label={t("नीचे ले जाएँ", `Move ${title} down`)}
                    onClick={() => move(index, index + 1)}
                  >
                    <ArrowDown className="size-3.5" />
                  </IconButton>
                  <IconButton
                    disabled={!removable}
                    label={
                      removable
                        ? t("श्रृंखला से हटाएँ", `Remove ${title} from the chain`)
                        : t("पहले ऊपर का स्तर हटाएँ", `Remove ${nameOf(roleKey(tier, n))} first`)
                    }
                    onClick={() => remove(node)}
                  >
                    <X className="size-3.5" />
                  </IconButton>
                </div>
              </li>
            );
          })}

          {chain.length === 0 ? (
            <li className="relative ml-8 py-3 text-sm text-muted">
              {t("कम से कम एक भूमिका जोड़ें।", "Add at least one role to build the chain.")}
            </li>
          ) : null}
        </ol>

        {unused.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5 border-t border-rule/70 px-4 py-3">
            <span className="mr-1 text-xs text-muted">{t("श्रृंखला में नहीं:", "Not in the chain:")}</span>
            {unused.map((tier) => (
              <button
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-rule px-2.5 py-1 text-xs font-medium text-ink transition-colors hover:border-brand-border hover:bg-brand-light hover:text-brand-ink"
                key={tier}
                onClick={() => add(tier)}
                type="button"
              >
                <Plus aria-hidden="true" className="size-3" />
                {label(tier)}
              </button>
            ))}
          </div>
        ) : null}
      </div>
  );
}

function IconButton({
  label,
  onClick,
  disabled = false,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      aria-label={label}
      className="grid size-7 place-items-center rounded-md text-muted transition-colors hover:bg-ink/[0.06] hover:text-ink disabled:pointer-events-none disabled:opacity-30"
      disabled={disabled}
      onClick={onClick}
      title={label}
      type="button"
    >
      <span aria-hidden="true">{children}</span>
    </button>
  );
}
